import { useEffect, useState } from 'react';
import { Upload } from 'lucide-react';

import { useApi } from '../../lib/api/api-context.js';

const MAX_LOGO_SIZE_BYTES = 26_214_400;

type ImageStatus = 'idle' | 'loaded' | 'error';

interface AttachmentUploadResponse {
  readonly id: string;
  readonly url: string;
  readonly signed_url: string;
}

interface LogoUploadProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly propertyId: string | undefined;
  readonly disabled: boolean | undefined;
  readonly error: string | undefined;
}

function apiBase(): string {
  const viteBase = import.meta.env.VITE_API_BASE_URL;
  if (viteBase !== undefined && viteBase !== '') {
    return viteBase;
  }
  return import.meta.env.DEV
    ? `${window.location.protocol}//${window.location.hostname}:3000`
    : window.location.origin;
}

export function LogoUpload({
  value,
  onChange,
  propertyId,
  disabled,
  error,
}: LogoUploadProps): React.JSX.Element {
  const api = useApi();
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [imageStatus, setImageStatus] = useState<ImageStatus>('idle');

  const previewUrl = value.startsWith('http') || value.startsWith('/') ? value : '';
  const imageSrc = previewUrl.startsWith('/') ? `${apiBase()}${previewUrl}` : previewUrl;

  useEffect(() => {
    if (previewUrl === '') return undefined;
    const img = new Image();
    img.onload = () => {
      setImageStatus('loaded');
      setLoadError(null);
    };
    img.onerror = () => {
      setImageStatus('error');
      setLoadError('The logo preview could not be loaded.');
    };
    img.src = imageSrc;
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [imageSrc, previewUrl]);

  const upload = async (file: File): Promise<void> => {
    if (!file.type.startsWith('image/')) {
      setUploadError('Please choose an image file.');
      return;
    }
    if (file.size > MAX_LOGO_SIZE_BYTES) {
      setUploadError('Logos must be 25 MB or smaller.');
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const path = propertyId ? '/v1/attachments/managed' : '/v1/attachments';
      if (propertyId) {
        form.append('property_id', propertyId);
        form.append('category', 'logo');
      }
      const response = await api.postForm<AttachmentUploadResponse>(path, form);
      onChange(response.signed_url);
    } catch (cause) {
      setUploadError(cause instanceof Error ? cause.message : 'The logo could not be uploaded.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-border p-4">
        <div className="flex flex-col items-stretch gap-4 sm:flex-row sm:items-start">
          {previewUrl !== '' ? (
            <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-md border border-border bg-muted">
              {imageStatus !== 'loaded' ? (
                <div className="flex h-full w-full items-center justify-center p-2 text-center text-xs text-muted-foreground">
                  {imageStatus === 'idle' ? 'Loading…' : 'Preview failed'}
                </div>
              ) : null}
              {imageStatus === 'loaded' ? (
                <img className="h-full w-full object-contain" src={imageSrc} alt="" />
              ) : null}
            </div>
          ) : null}

          <label className="flex min-h-24 flex-1 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-border p-4 text-center hover:border-primary">
            <Upload className="mb-2 h-5 w-5 text-muted-foreground" aria-hidden="true" />
            <span className="text-sm font-medium">
              {uploading
                ? 'Uploading logo…'
                : previewUrl !== ''
                  ? 'Drop a new logo or click to replace'
                  : 'Drop logo here or click to browse'}
            </span>
            <span className="mt-1 text-xs text-muted-foreground">JPG, PNG or WebP up to 25 MB</span>
            <input
              className="sr-only"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              disabled={disabled ?? uploading}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file !== undefined) void upload(file);
                event.target.value = '';
              }}
            />
          </label>
        </div>
      </div>

      {loadError !== null ? (
        <p className="rounded bg-destructive/10 p-3 text-sm text-destructive" role="alert">
          {loadError}
        </p>
      ) : null}
      {uploadError !== null || error !== undefined ? (
        <p className="rounded bg-destructive/10 p-3 text-sm text-destructive" role="alert">
          {uploadError ?? error}
        </p>
      ) : null}
    </div>
  );
}
